<section class="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-10">
  @if (loading) {
    <article class="rounded-2xl border border-slate-700 bg-slate-900/60 p-8 text-center">
      <div class="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-amber-300 border-t-transparent"></div>
      <h1 class="text-xl font-semibold text-slate-100">Loading product</h1>
      <p class="mt-2 text-sm text-slate-300">Please wait a moment.</p>
    </article>
  } @else if (hasError) {
    <article class="rounded-2xl border border-red-500/40 bg-red-950/30 p-8 text-center">
      <h1 class="text-xl font-semibold text-red-200">Unable to load product</h1>
      <p class="mt-2 text-sm text-red-100/85">Try again in a few seconds.</p>
      <button
        type="button"
        class="clp-btn clp-btn-warning mt-5"
        (click)="reload()">
        Retry
      </button>
    </article>
  } @else if (notFound || !digitalProduct) {
    <article class="rounded-2xl border border-slate-700 bg-slate-900/60 p-8 text-center">
      <h1 class="text-xl font-semibold text-slate-100">Product not found</h1>
      <p class="mt-2 text-sm text-slate-300">
        We could not find a digital product for <span class="font-semibold text-slate-100">{{ getPublicHandle() }}</span> using slug <span class="font-semibold text-slate-100">{{ slug }}</span>.
      </p>
      <a
        [routerLink]="['/']"
        class="clp-btn clp-btn-warning mt-5 inline-flex">
        Back to home
      </a>
    </article>
  } @else {
    <article class="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/60 shadow-lg">
      <div class="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div class="relative min-h-64 bg-slate-950">
          @if (resolveCoverImageUrl(digitalProduct.coverUrl); as coverUrl) {
            <img
              [src]="coverUrl"
              [alt]="digitalProduct.name ?? 'Digital product cover'"
              class="h-full w-full object-cover" />
          } @else {
            <div class="flex h-full min-h-64 items-center justify-center text-slate-500">
              <i class="fa-solid fa-box-open text-4xl"></i>
            </div>
          }
        </div>

        <div class="p-6 md:p-8">
          <p class="text-xs uppercase tracking-wide text-slate-400">Digital Product</p>
          <h1 class="mt-2 text-3xl font-semibold text-slate-100">{{ digitalProduct.name }}</h1>
          <p class="mt-2 text-sm text-slate-300">By {{ getPublicHandle() }}</p>

          <div class="mt-4 flex flex-wrap gap-2">
            <span
              class="rounded-full border px-2.5 py-1 text-xs font-semibold"
              [ngClass]="getVisibilityBadgeClass(digitalProduct.visibility)">
              {{ getVisibilityLabelKey(digitalProduct.visibility) | translate }}
            </span>
            <span
              class="rounded-full border px-2.5 py-1 text-xs font-semibold"
              [ngClass]="getBillingTypeBadgeClass(digitalProduct.billingType)">
              {{ getBillingTypeLabelKey(digitalProduct.billingType) | translate }}
            </span>
          </div>

          <div class="mt-6 rounded-xl border border-slate-700 bg-slate-950/60 p-4">
            @if (isRecurringProduct(digitalProduct)) {
              <p class="text-2xl font-semibold text-slate-100">
                {{ digitalProduct.recurringFee ?? 0 | number:'1.2-2' }} {{ digitalProduct.currency ?? 'USD' }}
              </p>
              <p class="mt-1 text-sm text-slate-300">
                Billed every {{ digitalProduct.recurringIntervalCount ?? 1 }} {{ getRecurringIntervalLabelKey(digitalProduct.recurringInterval) | translate | lowercase }}
              </p>
            } @else {
              <p class="text-2xl font-semibold text-slate-100">
                {{ digitalProduct.oneTimeFee ?? 0 | number:'1.2-2' }} {{ digitalProduct.currency ?? 'USD' }}
              </p>
              <p class="mt-1 text-sm text-slate-300">One-time purchase</p>
            }
          </div>

          @if (digitalProduct.description) {
            <p class="mt-6 whitespace-pre-line text-sm leading-relaxed text-slate-200">
              {{ digitalProduct.description }}
            </p>
          }

          <div class="mt-6 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
            <div class="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <p class="text-xs uppercase tracking-wide text-slate-500">Slug</p>
              <p class="mt-1 font-medium text-slate-100">{{ digitalProduct.slug ?? slug }}</p>
            </div>
            <div class="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <p class="text-xs uppercase tracking-wide text-slate-500">Published At</p>
              <p class="mt-1 font-medium text-slate-100">{{ getPublishedDate(digitalProduct.publishedAt) | date:'mediumDate' }}</p>
            </div>
          </div>
        </div>
      </div>
    </article>
  }
</section>
